// Frontend/src/contexts/AuthContext.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { setAccessToken, clearAccessToken } from "@/api/client";
import { login as apiLogin, fetchMe, logout as apiLogout, Me } from "@/api/auth";
import { queryClient } from "@/lib/queryClient";
import { clearAppCaches } from "@/lib/clearCaches";

type AuthContextType = {
  user: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const ACCESS_KEY = "access_token";
const ROLE_KEY = "role";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function saveSession(access?: string | null, role?: Me["role"] | null) {
  if (access) sessionStorage.setItem(ACCESS_KEY, access);
  if (role) sessionStorage.setItem(ROLE_KEY, role);
}

function clearSession() {
  try {
    // Clear ALL sessionStorage to remove any app-specific keys
    // This ensures cg_sa_payments, condo_id, and any future keys are removed
    sessionStorage.clear();
  } catch (error) {
    console.error('Failed to clear sessionStorage:', error);
    // Fallback: Try to remove known keys individually
    try {
      sessionStorage.removeItem(ACCESS_KEY);
      sessionStorage.removeItem(ROLE_KEY);
      sessionStorage.removeItem('cg_sa_payments');
      sessionStorage.removeItem('condo_id');
    } catch (fallbackError) {
      console.error('Fallback clear also failed:', fallbackError);
    }
  }
}

function clearResidentLocalSession() {
  try {
    localStorage.removeItem('resident_access_token');
    localStorage.removeItem('resident_refresh_token');
    localStorage.removeItem('resident_id');
    localStorage.removeItem('resident_name');
    localStorage.removeItem('resident_email');
  } catch (error) {
    console.error('Failed to clear resident local session:', error);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      // Silent refresh using httpOnly cookie
      const res = await api.post<{ access: string }>("/auth/refresh/");
      const access = res.data?.access;
      if (access) {
        setAccessToken(access);
        // Persist for router loaders
        saveSession(access, null);
        const me = await fetchMe();
        setUser(me);
        // Save role for loaders that gate by role
        saveSession(access, me.role);
      } else {
        clearAccessToken();
        clearSession();
        setUser(null);
      }
    } catch {
      clearAccessToken();
      clearSession();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // On mount, try to silently restore a session
    (async () => {
      await refreshSession();
      setLoading(false);
    })();
  }, [refreshSession]);

  const login = useCallback(async (email: string, password: string) => {
    // Clear any previous user's cached data before loading the new user's data.
    // This prevents stale data from a previous session appearing momentarily.
    queryClient.clear();
    const { access, user } = await apiLogin(email, password);
    // In-memory for axios
    setAccessToken(access);
    setUser(user);
    // Persist for router loaders (hash router loaders run before context renders)
    saveSession(access, user.role);
    return user;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call backend logout first (while token is still valid)
      await apiLogout();
    } catch (error) {
      console.warn('Backend logout failed; clearing the local session.');
    } finally {
      // ALWAYS clear local state, even if backend fails
      clearAccessToken();
      clearSession(); // Now clears ALL sessionStorage
      clearResidentLocalSession();
      // *** Clear ALL cached query data so the next user sees no stale data ***
      queryClient.clear();
      // queryClient.clear() empties memory; this empties disk. The service
      // worker caches /api/ responses keyed by URL alone, with nothing in the
      // key identifying the user, so without this the previous user's data
      // stays readable on a shared device. Deliberately not awaited: logout
      // must not hang on storage.
      void clearAppCaches();
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({ user, loading, login, logout, refreshSession }),
    [user, loading, login, logout, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
